import { Component, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { AbstractControl, NgForm } from '@angular/forms';
import { BlueyonderUser } from '../shared/blueyonder-user/blueyonder-user.model';
import { BlueyonderCountry } from '../shared/blueyonder-user/blueyonder-user-country.model';
import { BlueyonderUserService } from '../shared/blueyonder-user/blueyonder-user.service';
import { NavigationService } from 'app/shared/services/navigation.service';
import { Carrier } from '../shared/carrier.model';
import { AuthenticationService } from 'app/shared/services/authentication.service';
import { BlueyonderLogisticGroup } from '../shared/blueyonder-user/blueyonder-logistic-group.model';
import { ActivatedRoute } from '@angular/router';
import { LoaderService } from 'app/shared/services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, finalize } from 'rxjs/operators';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { Subject, Subscription } from 'rxjs';

const MAX_LENGTH_USER_CODE = 10;
const MAX_LENGTH_USER_NAME = 50;

@Component({
  selector: 'app-blueyonder-user-form',
  templateUrl: './blueyonder-user-form.component.html',
  styleUrls: ['./blueyonder-user-form.component.scss']
})
export class BlueyonderUserFormComponent implements OnInit {
  blueyonderUser: BlueyonderUser            = new BlueyonderUser();
  carrierCode: string;
  userCarrier: Carrier;
  canSeeAllCarriers                         = false;
  logisticGroups: BlueyonderLogisticGroup[] = [];
  countries: BlueyonderCountry[]            = [];
  formProcessingBlocked                     = false;
  userCodeSubject                           = new Subject();
  userCodeSubscription: Subscription;
  showTooltip                               = false;
  creating                                  = false;

  maxLengthUserCode = MAX_LENGTH_USER_CODE;
  maxLengthUserName = MAX_LENGTH_USER_NAME;

  @ViewChild('blueyonderUserForm')
  form: NgForm;

  constructor(
    public location: Location,
    private translate: TranslateService,
    private blueyonderUserService: BlueyonderUserService,
    private navigation: NavigationService,
    private authService: AuthenticationService,
    private route: ActivatedRoute,
    private loaderService: LoaderService,
    private toastr: ToastrService,
    private errorHandler: ErrorHandlerService,
  ) {
    this.blueyonderUser.languageCode = 'en';
    this.userCarrier                 = this.authService.getUserProfile().carrier;
    this.canSeeAllCarriers           = this.authService.canManageBlueyonderUserAllCarriers();

    if (this.canSeeAllCarriers) {
      this.route.queryParams.subscribe(params => {
        this.carrierCode = params.carrierId;
      });
    } else {
      this.carrierCode = this.userCarrier.id;
    }

    this.blueyonderUserService.getLogisticGroup()
      .subscribe(res => this.logisticGroups = res);
    this.blueyonderUserService.getCountries()
      .subscribe(countries => this.countries = countries);

    this.userCodeSubscription = this.userCodeSubject.pipe(debounceTime(1000))
      .subscribe(() => {
        this.doBlueyonderUserExist(this.form, 'byUser-userCode');
      });
  }

  ngOnInit(): void {
  }

  userCodeChanged() {
    this.userCodeSubject.next();
  }

  doBlueyonderUserExist(form: NgForm, userCodeFieldName: string) {
    const blueyonderUserCode = form.form.controls[userCodeFieldName];
    if (!blueyonderUserCode.hasError('pattern') && !blueyonderUserCode.hasError('required')) {
      this.loaderService.startLoad();
      this.blueyonderUserService.doBlueyonderUserExist(this.blueyonderUser.userCode)
        .pipe(finalize(() => this.loaderService.stopLoad()))
        .subscribe(res => {
          if (res && res.exists) {
            blueyonderUserCode.setErrors({ userAlreadyExist: true });
          } else {
            blueyonderUserCode.updateValueAndValidity();
          }
        });
    }
  }

  processForm() {
    if (this.form.valid && !this.creating) {
      this.creating = true;
      this.loaderService.startLoad();
      this.blueyonderUserService.create(this.blueyonderUser, this.carrierCode, this.canSeeAllCarriers)
        .pipe(finalize(() => {
          this.creating = false;
          this.loaderService.stopLoad();
        }))
        .subscribe(() => {
          this.toastr.success(this.translate.instant(
            'user-dashboard.blueyonder-user.list.successes.create',
            { userName: this.blueyonderUser.userName }
          ));
          this.navigation.blueyonderUsers().list(this.carrierCode).go();
        }, error => {
          const castedError = JSON.parse(error?.error?.message);
          const errorCode = castedError ? castedError.error.find(err => err.severity === 'Error').code : null;
          this.errorHandler.handleHttpError(
            error,
            this.returnErrorMessageByBlueyonderErrorCode(errorCode),
            false
          )
        });
    }
  }

  returnErrorMessageByBlueyonderErrorCode(errorCode: number) {
    switch (errorCode) {
      case 3:
        return this.translate.instant('user-dashboard.blueyonder-user.list.error.logistic-group-non-existing');
      case 4:
        return this.translate.instant('user-dashboard.blueyonder-user.list.error.user-id-existing');
      default:
        return this.translate.instant('user-dashboard.blueyonder-user.list.error.create', { userName: this.blueyonderUser.userName })
    }
  }

  toggleTooltip() {
    this.showTooltip = !this.showTooltip;
  }
}
